import supabaseClient from '@/api/supabaseClient.ts';
import { FunctionsHttpError, SupabaseClient } from '@supabase/supabase-js';
import { useMutation } from '@tanstack/react-query';
import { VerifyEmailRequest } from '@/types/request/VerifyEmailRequest';
import { BackendErrorResponse } from '@/types/response/BackendErrorResponse';
import { BackendSuccessResponse } from '@/types/response/BackendSuccessResponse';

const useVerifyEmail = () => {
  const client = supabaseClient;

  const mutationFn = async (request: VerifyEmailRequest) => {
    return verifyEmail(client, request);
  };

  return useMutation({
    mutationFn,
  });
};

export default useVerifyEmail;

async function verifyEmail(
  client: SupabaseClient,
  request: VerifyEmailRequest
): Promise<BackendSuccessResponse<{ message: string }>> {
  const { error, data } = await client.auth.verifyOtp({
    email: request.email,
    token: request.code,
    type: 'email',
  });

  if (error) throw new Error(error.message);

  if (!data.user) throw new Error('No user returned');

  const { data: backendData, error: backendError } = await client.functions.invoke('email_verified', {
    body: { id: data.user.id },
  });

  if (backendError && backendError instanceof FunctionsHttpError) {
    let message = 'An unexpected error occurred';
    try {
      const errorResponse: BackendErrorResponse = await backendError.context.json();
      if (Array.isArray(errorResponse?.errors)) {
        message = errorResponse.errors.join(', ');
      } else if (typeof errorResponse?.errors === 'string') {
        message = errorResponse.errors;
      }
    } catch {
      // Response was not valid JSON
    }
    throw new Error(message);
  }

  return backendData;
}
